<template>
	<div class="home">
		<!-- 导航栏 -->
		<Header />

		<!-- Banner轮播 -->
		<section class="banner-carousel">
			<el-carousel height="700px" :interval="5000" arrow="always" indicator-position="outside">
				<el-carousel-item v-for="banner in bannerList" :key="banner.bannerId">
					<a v-if="banner.linkUrl" :href="banner.linkUrl" target="_blank" class="banner-link">
						<div class="banner-slide"
							:style="{ backgroundImage: `url(${getFullImageUrl(banner.imageUrl)})` }"></div>
					</a>
					<div v-else class="banner-slide"
						:style="{ backgroundImage: `url(${getFullImageUrl(banner.imageUrl)})` }"></div>
				</el-carousel-item>
			</el-carousel>
		</section>

		<!-- 企业介绍 -->
		<a v-if="introLinkUrl" :href="introLinkUrl" target="_blank" class="intro-link">
			<section class="intro" :style="introStyle"></section>
		</a>

		<section v-else class="intro" :style="introStyle"></section>

		<!-- SCI论文展示区 -->
		<section class="sci-showcase">
			<div class="sci-content">
				<!-- 左侧向上滚动 -->
				<div class="sci-scroll-col sci-scroll-left">
					<div class="sci-scroll-track" :style="{ transform: `translateY(${leftScrollOffset}px)` }">
						<div class="sci-card-wrapper" v-for="(card, idx) in leftCards" :key="'left-' + idx"
							:class="{ 'card-offset-left': idx % 2 === 0, 'card-offset-right': idx % 2 === 1 }">
							<div class="sci-card">
								<div class="sci-card-info">
									<div class="sci-card-factor">{{ card.journal }} |<span> {{ card.author }}</span></div>
									<div class="sci-card-factor">擅长：<span>{{ card.factor }}</span></div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- 中间人物 -->
				<div class="sci-center">
				</div>

				<!-- 右侧向上滚动 -->
				<div class="sci-scroll-col sci-scroll-right">
					<div class="sci-scroll-track" :style="{ transform: `translateY(${rightScrollOffset}px)` }">
						<div class="sci-card-wrapper" v-for="(card, idx) in rightCards" :key="'right-' + idx"
							:class="{ 'card-offset-right': idx % 2 === 0, 'card-offset-left': idx % 2 === 1 }">
							<div class="sci-card">
								<div class="sci-card-info">
									<div class="sci-card-factor">{{ card.journal }} | <span>{{ card.author }}</span></div>
									<div class="sci-card-factor">擅长：<span>{{ card.factor }}</span></div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="text-card">团队介绍：双一流高校博士技术顾问团队，覆盖环境治理、金属材料、新能源储能、建筑化工、有机/无机/复合功能材料全领域，提供结构表征、性能测试、失效分析、配方优化与数据分析全流程技术支撑。</div>
		</section>

		<div class="footer-grid-item footer-logo">
			<span class="footer-brand-name">合作校企</span>
		</div>
		<div class="logo-carousel-wrapper" @mousedown="startDrag" @mousemove="onDrag" @mouseup="stopDrag" @mouseleave="stopDrag">
			<div class="carousel-row" v-for="(row, rowIndex) in groupedLogos" :key="rowIndex">
				<div class="carousel-track" :style="{ transform: `translateX(${scrollOffsets[rowIndex]}px)` }">
					<div class="logo-item" v-for="logo in row" :key="logo.partnerId">
						<a v-if="logo.websiteUrl" :href="logo.websiteUrl" target="_blank" class="logo-link" @click.stop>
							<img :src="getFullImageUrl(logo.logoUrl)" class="logo-img" />
						</a>
						<img v-else :src="getFullImageUrl(logo.logoUrl)" class="logo-img" />
					</div>
				</div>
			</div>
		</div>
		<Footer />
	</div>
</template>

<script setup>
	import Header from '@/components/Header.vue'
	import Footer from '@/components/Footer.vue'
	import { ref, onMounted, onUnmounted, nextTick, computed } from 'vue'
	import { getBanners, getCompanyIntro, getPartner } from '@/api/website'

	const BASE_URL = process.env.VUE_APP_BASE_URL || ''

	const bannerList = ref([])
	const introImageUrl = ref('')
	const introLinkUrl = ref('')
	const logoList = ref([])
	const getFullImageUrl = (url) => {
		if (!url) return ''
		if (url.startsWith('http')) return url
		return BASE_URL + encodeURI(url)
	}

	const introStyle = computed(() => {
		const url = getFullImageUrl(introImageUrl.value)
		return url ? { backgroundImage: `url(${url})` } : {}
	})
	const sciCardsData = [
		{ journal: '热催化领域技术顾问', factor: '吸附表征测试、傅里叶红外光谱表征', author: '汪博士/中国石油大学'},
		{ journal: '多孔材料领域技术顾问', factor: '材料表征测试、表面增强拉曼', author: '张博士/南开大学' },
		{ journal: '电催化领域技术顾问', factor: '电化学工作站、XRD、XPS测试及谱图解析', author: '周博士/天津大学'},
		{ journal: '光催化领域技术顾问', factor: '紫外-可见漫反射、瞬态光电流、PL荧光表征', author: '吴博士/华东理工大学'},
		{ journal: '锂离子电池领域技术顾问', factor: '循环伏安、充放电测试、TEM/SEM微观形貌分析', author: '李博士/西安交通大学'},
		{ journal: '钠离子电池领域技术顾问', factor: '原位XRD、阻抗EIS、极片理化性能检测', author: '陈博士/中南大学' },
		{ journal: '固态电池领域技术顾问', factor: '界面阻抗、手套箱XPS,UPS表征', author: '黄博士/中国科学院大学'},
		{ journal: '宽禁带半导体领域技术顾问', factor: 'AFM、KPFM、霍尔测试，射频测试', author: '郑博士/西安电子科技大学'},
		{ journal: '钙钛矿光电领域技术顾问', factor: '瞬态吸收光谱、X射线衍射、荧光寿命测试', author: '江博士/南京大学'},
		{ journal: '高分子复合材料领域技术顾问', factor: 'GPC凝胶渗透色谱、DSC、TGA热重分析', author: '马博士/四川大学' },
		{ journal: '功能涂层领域技术顾问', factor: '划痕测试、接触角、X射线荧光XRF表征', author: '宋博士/哈尔滨工业大学'},
		{ journal: '第一性原理仿真领域技术顾问', factor: '催化界面、电极能带结构DFT理论计算', author: '王博士/西北工业大学'},
		{ journal: '分子动力学模拟领域技术顾问', factor: '高分子、电解液体系分子动力学建模分析', author: '姚博士/浙江大学'},
		{ journal: '无人机领域技术顾问', factor: '无人机整体设计及配套软件开发', author: '胡博士/西北工业大学' },
		{ journal: '人工智能识别领域技术顾问', factor: '计算机智能识别分析，数字孪生', author: '胡博士/哈尔滨工业大学'},
		{ journal: '水污染治理领域技术顾问', factor: '水质理化检测、污泥表征、污染物吸附降解分析', author: '林博士/同济大学'},
		{ journal: '大气环境检测领域技术顾问', factor: '颗粒物组分分析、VOCs检测、大气污染物溯源表征', author: '赵博士/浙江大学' },
		{ journal: '固废资源化领域技术顾问', factor: '固废热重分析、浸出毒性测试、固废改性表征', author: '孙博士/重庆大学'},
		{ journal: '土壤修复领域技术顾问', factor: '土壤元素Mapping、重金属形态分析、土壤孔隙结构表征', author: '杨博士/西北农林科技大学'},
		{ journal: '高强钢铁材料领域技术顾问', factor: '金相组织分析、EBSD取向表征、钢材力学性能测试', author: '刘博士/北京科技大学'},
		{ journal: '铝合金轻质材料领域技术顾问', factor: '铝合金析出相分析、疲劳断裂表征、压铸缺陷检测', author: '温博士/中南大学' },
		{ journal: '钛合金高温材料领域技术顾问', factor: '高温氧化表征、蠕变性能测试、钛合金微观结构分析', author: '薛博士/西北工业大学'},
		{ journal: '铜基导电材料领域技术顾问', factor: '铜合金纯度分析、导电率测试、应力腐蚀表征', author: '胡博士/江西理工大学'},
		{ journal: '氢能燃料电池领域技术顾问', factor: '氢氧催化表征、膜电极微观分析、燃料电池性能测试', author: '程博士/厦门大学'},
		{ journal: '光伏新能源领域技术顾问', factor: '晶硅缺陷检测、光伏材料透光率、老化失效表征', author: '夏博士/河海大学' },
		{ journal: '超级电容器领域技术顾问', factor: '多孔电极表征、循环稳定性测试、电容性能分析', author: '季博士/苏州大学'},
		{ journal: '岩石矿物检测领域技术顾问', factor: '矿物XRD物相分析、岩矿薄片鉴定、元素成分扫描', author: '方博士/中国地质大学'},
		{ journal: '岩土工程材料领域技术顾问', factor: '土体粒径分析、压实性能测试、岩土微观结构表征', author: '武博士/长安大学'},
		{ journal: '水泥基材料领域技术顾问', factor: '水化产物表征、混凝土孔隙分析、强度失效检测', author: '谷博士/东南大学' },
		{ journal: '耐火保温材料领域技术顾问', factor: '耐高温性能测试、热膨胀系数表征、耐火材料失效分析', author: '潘博士/武汉科技大学'},
		{ journal: '精细化工材料领域技术顾问', factor: '化工原料纯度检测、有机组分质谱分析、配方表征', author: '戴博士/华东理工大学' },
		{ journal: '防爆安全材料领域技术顾问', factor: '材料阻燃测试、热安全性分析、爆炸极限物性表征', author: '耿博士/南京工业大学' },
		{ journal: '粉体无机材料技术顾问', factor: '粉体粒度分布、比表面积BET、无机粉体形貌与缺陷表征', author: '黎博士/华南理工大学' },
		{ journal: '纳米无机材料技术顾问', factor: '纳米氧化物、纳米颗粒分散性、原位微观形貌表征', author: '任博士/复旦大学' },
		{ journal: '矿物无机材料技术顾问', factor: '非金属矿物物相分析、杂质定量、矿物改性结构表征', author: '童博士/合肥工业大学' },
		{ journal: '气凝胶多孔材料技术顾问', factor: '孔隙率测试、孔径分布、隔热无机材料性能表征', author: '盛博士/同济大学' },
		{ journal: '陶瓷功能材料技术顾问', factor: '结构陶瓷致密性、介电性能、高温力学性能测试', author: '聂博士/中南大学' },
		{ journal: '通用高分子材料技术顾问', factor: '塑料、树脂基材热性能、流变性能、老化失效表征', author: '吕博士/郑州大学' },
		{ journal: '有机薄膜材料技术顾问', factor: '高分子薄膜厚度、粗糙度、透光性、界面结合表征', author: '卞博士/苏州大学' },
		{ journal: '橡胶弹性材料技术顾问', factor: '橡胶交联度、拉伸回弹、疲劳老化、硬度力学测试', author: '纪博士/青岛科技大学' },
		{ journal: '水性有机材料技术顾问', factor: '水性涂料、乳液粒径、粘度稳定性、成膜性能分析', author: '费博士/江南大学' },
		{ journal: '有机荧光材料技术顾问', factor: '有机发光分子光谱、荧光量子产率、激发态结构表征', author: '温博士/中山大学' },
		{ journal: '有机吸附材料技术顾问', factor: '有机多孔树脂吸附、官能团分析、污染物吸附机理表征', author: '肖博士/湖南大学' },
		{ journal: '有机防腐材料技术顾问', factor: '有机涂层阻抗、盐雾腐蚀、界面失效、附着力测试', author: '邢博士/武汉理工大学' },
		{ journal: '有机无机复合材料技术顾问', factor: '复合材料界面结构、填料分散性、力学增强机理分析', author: '俞博士/东华大学' },
		{ journal: '功能改性复材技术顾问', factor: '有机改性无机填料、复合体系热稳定性、耐候性表征', author: '甘博士/西南交通大学' }
	]

	const leftCards = computed(() => {
		const half = Math.ceil(sciCardsData.length / 2)
		const cards = sciCardsData.slice(0, half)
		return [...cards, ...cards]
	})

	const rightCards = computed(() => {
		const half = Math.ceil(sciCardsData.length / 2)
		const cards = sciCardsData.slice(half)
		return [...cards, ...cards]
	})

	const leftScrollOffset = ref(0)
	const rightScrollOffset = ref(0)
	let sciAnimationId = null
	let sciCardHeight = 0
	let singleColHeight = 0

	const animateSciScroll = () => {
		const scrollSpeed = 0.5
		leftScrollOffset.value -= scrollSpeed
		rightScrollOffset.value -= scrollSpeed * 0.8

		if (singleColHeight > 0) {
			if (Math.abs(leftScrollOffset.value) >= singleColHeight) {
				leftScrollOffset.value = leftScrollOffset.value % singleColHeight
			}
			if (Math.abs(rightScrollOffset.value) >= singleColHeight) {
				rightScrollOffset.value = rightScrollOffset.value % singleColHeight
			}
		}

		sciAnimationId = requestAnimationFrame(animateSciScroll)
	}

	const initSciScroll = async () => {
		await nextTick()
		const dom = document.querySelector('.sci-card')
		if (dom) {
			sciCardHeight = dom.offsetHeight + 30
			const half = Math.ceil(sciCardsData.length / 2)
			singleColHeight = sciCardHeight * half
		}
	}

	const ROW_COUNT = 3
	const ITEMS_PER_ROW = 6
	const SCROLL_SPEED = 1

	const scrollOffsets = ref([0, 0, 0])
	let animationId = null
	let itemWidth = 0
	let singleSetWidth = 0
	let isPaused = false
	let isDragging = false
	let startX = 0

	const groupedLogos = computed(() => {
		const res = []
		for (let i = 0; i < ROW_COUNT; i++) {
			const start = i * ITEMS_PER_ROW
			const end = start + ITEMS_PER_ROW
			const row = logoList.value.slice(start, end)
			res.push([...row, ...row])
		}
		return res
	})

	const animateScroll = () => {
		if (!isDragging) {
			for (let i = 0; i < ROW_COUNT; i++) {
				scrollOffsets.value[i] -= SCROLL_SPEED
				if (singleSetWidth > 0 && Math.abs(scrollOffsets.value[i]) >= singleSetWidth) {
					scrollOffsets.value[i] = scrollOffsets.value[i] % singleSetWidth
				}
			}
		}
		animationId = requestAnimationFrame(animateScroll)
	}

	const startDrag = (e) => {
		isDragging = true
		startX = e.clientX
	}

	const onDrag = (e) => {
		if (!isDragging) return
		const currentX = e.clientX
		const deltaX = currentX - startX
		startX = currentX
		for (let i = 0; i < ROW_COUNT; i++) {
			scrollOffsets.value[i] += deltaX
			if (singleSetWidth > 0) {
				if (scrollOffsets.value[i] > 0) {
					scrollOffsets.value[i] = scrollOffsets.value[i] % singleSetWidth
				} else if (Math.abs(scrollOffsets.value[i]) >= singleSetWidth) {
					scrollOffsets.value[i] = scrollOffsets.value[i] % singleSetWidth
				}
			}
		}
	}

	const stopDrag = () => {
		isDragging = false
	}

	const pauseScroll = () => {
		isPaused = true
	}

	const resumeScroll = () => {
		isPaused = false
	}

	const loadData = async () => {
		try {
			const [bannerRes, introRes, getData] = await Promise.all([
				getBanners(), getCompanyIntro(), getPartner()
			])
			if (bannerRes.code === 200 && bannerRes.rows?.length > 0) {
				const processedBanners = []
				bannerRes.rows
					.filter(b => b.status === '0')
					.sort((a, b) => (a.orderNum || 0) - (b.orderNum || 0))
					.forEach(banner => {
						if (banner.imageUrl && banner.imageUrl.includes(',')) {
							const imageUrls = banner.imageUrl.split(',').map(url => url.trim()).filter(url => url)
							imageUrls.forEach((url, index) => {
								processedBanners.push({ ...banner, bannerId: `${banner.bannerId}-${index}`, imageUrl: url })
							})
						} else {
							processedBanners.push(banner)
						}
					})
				bannerList.value = processedBanners
			}

			if (introRes.code === 200 && introRes.rows?.length > 0) {
				const intro = introRes.rows.find(i => i.status === '0') || introRes.rows[0]
				if (intro.imageUrl) introImageUrl.value = intro.imageUrl
				if (intro.linkUrl) introLinkUrl.value = intro.linkUrl
			}
			if (getData.code === 200 && getData.rows) {
				logoList.value = getData.rows
				await nextTick()
				const dom = document.querySelector('.logo-item')
				if (dom) {
					itemWidth = dom.offsetWidth
					singleSetWidth = itemWidth * ITEMS_PER_ROW
				}
			}
		} catch (error) {
			console.error('加载数据失败:', error)
		}
	}

	onMounted(async () => {
		await nextTick()
		animateScroll()
		animateSciScroll()
		loadData()
		initSciScroll()
	})

	onUnmounted(() => {
		if (animationId) cancelAnimationFrame(animationId)
		if (sciAnimationId) cancelAnimationFrame(sciAnimationId)
	})
</script>

<style scoped>
	* {
		margin: 0;
		padding: 0;
		box-sizing: border-box;
	}
	.text-card{
		width: 90%;
		margin: auto;
		font-weight: 600;
		font-size: 28px;
		color: #333;
	}
	@media (min-width: 1700px) {
	    .text-card {
	       margin-top: 55px;
	    }
	}
	.home {
		font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
		line-height: 1.5;
		color: #333;
		margin: 0 auto;
		background: #fff;
		box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
	}

	::v-deep .el-carousel__indicators--outside {
		display: none;
	}

	.banner-carousel {
		margin-top: 100px;
	}

	.banner-carousel .el-carousel {
		width: 100%;
	}

	.banner-slide {
		width: 100%;
		height: 100%;
		background-size: cover;
		background-position: center;
		position: relative;
	}

	.banner-link {
		display: block;
		width: 100%;
		height: 100%;
		cursor: pointer;
	}

	.logo-carousel-wrapper {
		width: 100%;
		overflow: hidden;
		padding: 30px 0;
		background: #fff;
		cursor: grab;
		user-select: none;
	}

	.logo-carousel-wrapper:active {
		cursor: grabbing;
	}

	.carousel-row {
		width: 100%;
		overflow: hidden;
		margin-bottom: 20px;
	}

	.carousel-row:last-child {
		margin-bottom: 0;
	}

	.carousel-track {
		display: flex;
	}

	.logo-item {
		flex: 0 0 auto;
		width: calc(100% / 6);
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 10px;
		box-shadow: rgba(0, 0, 0, 0.05) 0px 4px 12px 2px;
		background: #fff;
		margin: 10px;
	}

	.logo-link {
		display: flex;
		align-items: center;
		justify-content: center;
		width: 100%;
		height: 90px;
		background-color: #fff;
		box-shadow: 0 4px 12px 2px rgba(0, 0, 0, .05);
		padding: 10px;
	}

	.logo-img {
		max-height: 70px;
		max-width: 90%;
		object-fit: contain;
	}

	.intro-link {
		display: block;
		text-decoration: none;
	}

	.intro {
		height: 876px;
		background-size: cover;
		background-position: center;
		position: relative;
		overflow: hidden;
	}

	.footer-grid-item {
		display: flex;
		padding: 0;
	}

	.footer-logo {
		align-items: center;
		height: 140px;
		max-width: 1400px;
		margin: auto;
		padding: 0 60px;
	}

	.footer-brand-name {
		font-size: 42px;
		font-weight: 700;
		color: #333;
		letter-spacing: 5px;
	}

	.sci-showcase {
		width: 100%;
		height: 1000px;
		background: #f8f9fb;
		padding: 40px 0;
		position: relative;
		background-image: url('/src/assets/2.png');
		background-size: 100% 100%;
	}
	@media (min-width: 1700px) {
	    .sci-showcase {
	        height: 1202px; 
			padding: 104px 0;
	    }
	}

	.sci-header {
		text-align: center;
		margin-bottom: 50px;
	}

	.sci-title {
		font-size: 36px;
		font-weight: 700;
		color: #333;
		margin: 0 0 10px 0;
	}

	.sci-subtitle {
		font-size: 16px;
		color: #999;
		margin: 0 0 15px 0;
		letter-spacing: 1px;
	}

	.sci-desc {
		font-size: 15px;
		color: #333;
		margin: 0;
	}

	.sci-content {
		display: flex;
		align-items: center;
		justify-content: space-between;
		max-width: 1400px;
		margin: 0 auto;
		padding: 0 60px;
		gap: 40px;
		position: relative;
		overflow: hidden;
	}

	.sci-scroll-col {
		flex: 1;
		height: 840px;
		/* overflow: hidden; */
		position: relative;
	}

	.sci-scroll-track {
		display: flex;
		flex-direction: column;
		gap: 10px;
		padding: 15px 0;
	}

	.sci-card-wrapper {
		width: 404px;
		padding:5px;
		margin: 20px 0;
		height: 111px;
		box-sizing: border-box;
		overflow: hidden;
		transition: all 0.3s;
		
	}

	.sci-card-wrapper.card-offset-left {
		transform: translateX(-30px);
	}

	.sci-card-wrapper.card-offset-right {
		transform: translateX(30px);
	}

	.sci-scroll-left .sci-card-wrapper.card-offset-left {
		transform: translateX(-50px);
	}

	.sci-scroll-left .sci-card-wrapper.card-offset-right {
		transform: translateX(10px);
	}

	.sci-scroll-right .sci-card-wrapper.card-offset-left {
		transform: translateX(-10px);
	}

	.sci-scroll-right .sci-card-wrapper.card-offset-right {
		transform: translateX(50px);
	}

	.sci-card {
		display: flex;
		align-items: center;
		gap: 15px;
		padding: 25px 41px;
		height: 111px;
		box-sizing: border-box;
		position: relative;
		background-image: url('/src/assets/3.png');
		background-size: 100% 100%;
	}

	.sci-card-cover {
		width: 48px;
		height: 48px;
		flex-shrink: 0;
		border-radius: 8px;
		overflow: hidden;
		background: #f0f0f0;
	}

	.sci-card-cover img {
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	.sci-card-info {
		flex: 1;
		min-width: 0;
	}

	.sci-card-journal {
		font-size: 14px;
		color: #333;
		font-weight: 500;
		margin-bottom: 3px;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.sci-card-factor {
		font-size: 14px;
		color: #333;
		font-weight: 600;
		margin-bottom: 3px;
	}

	.sci-card-factor span {
		font-weight: 500;
	}

	.sci-card-author {
		font-size: 12px;
		color: #888;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.sci-center {
		flex-shrink: 0;
		width: 320px;
		display: flex;
		flex-direction: column;
		align-items: center;
		position: relative;
		z-index: 2;
	}

	.sci-graduate {
		width: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		position: relative;
	}

	.sci-graduate::before {
		content: '';
		position: absolute;
		width: 350px;
		height: 350px;
		background: radial-gradient(circle, rgba(74, 144, 226, 0.1) 0%, transparent 70%);
		border-radius: 50%;
		z-index: -1;
	}

	.graduate-img {
		width: 100%;
		max-width: 300px;
		object-fit: contain;
	}

	.sci-more-link {
		margin-top: 10px;
	}

	.sci-more-link a {
		color: #c0392b;
		font-size: 16px;
		text-decoration: none;
		font-weight: 500;
	}

	.sci-more-link a:hover {
		text-decoration: underline;
	}
</style>